/**
 * Reset step 1 — the address the code goes to. Focuses its field on arrival (including when the
 * user comes BACK here from the code step), because the dialog's content was replaced under them.
 *
 * @module @puffin-core/login-shell/components/passwordReset/ResetEmailStep
 */

import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ResetErrorAlert } from './ResetErrorAlert.jsx';
import { ResetStepIndicator } from './ResetStepIndicator.jsx';

/**
 * @param {Object} props
 * @param {Object} props.flow - `usePasswordResetFlow`'s return
 * @param {Function} props.translate
 * @param {React.ReactNode} props.captchaSlot - the host's CaptchaField, or `null` (the modal always
 *   passes one or the other)
 * @returns {React.JSX.Element}
 */
export function ResetEmailStep({ flow, translate, captchaSlot }) {
  const { state } = flow;
  const emailRef = useRef(null);
  useEffect(() => {
    emailRef.current.focus();
  }, []);

  return (
    <form
      onSubmit={flow.handleInitiate}
      aria-label={translate('modal.passwordReset.form.email')}
      noValidate
    >
      <ResetStepIndicator current={1} total={3} translate={translate} />
      <p className="text text-sm text-muted mb-4">
        {translate('modal.passwordReset.multiStep.emailBody')}
      </p>
      <ResetErrorAlert error={state.error} translate={translate} />
      <div className="form-group">
        <label className="form-label" htmlFor="reset-email">
          {translate('login.email.label')}
        </label>
        <input
          ref={emailRef}
          className="form-input"
          id="reset-email"
          type="email"
          value={state.email}
          onChange={(e) => flow.setField('email', e.target.value)}
          aria-required="true"
          autoComplete="email"
          disabled={state.isLoading}
          data-testid="reset-email-input"
        />
      </div>
      {captchaSlot && (
        <div className="form-group" data-testid="reset-captcha-slot">
          {captchaSlot}
        </div>
      )}
      <button
        type="submit"
        className="btn btn-primary btn-lg w-full"
        disabled={!state.email.trim() || state.isLoading}
        aria-busy={state.isLoading ? 'true' : 'false'}
        data-testid="reset-email-submit"
      >
        {state.isLoading
          ? translate('modal.passwordReset.submit.loading')
          : translate('modal.passwordReset.multiStep.emailSubmit')}
      </button>
    </form>
  );
}

ResetEmailStep.propTypes = {
  flow: PropTypes.object.isRequired,
  translate: PropTypes.func.isRequired,
  captchaSlot: PropTypes.node,
};

export default ResetEmailStep;
